import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export const generateComplianceReport = (shipment, temperatureData = []) => {
  const doc = new jsPDF();
  
  // Note: autoTable is now called as a function with doc as first argument
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('SmartPallet', 20, 20);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Cold Chain Compliance Report', 20, 30);
  
  // Report info
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 70, 20);
  doc.text(`Report ID: RPT-${Date.now().toString(36).toUpperCase()}`, pageWidth - 70, 27);
  
  // Shipment Details Section
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Shipment Details', 20, 55);
  
  doc.setDrawColor(226, 232, 240);
  doc.line(20, 58, pageWidth - 20, 58);
  
  const details = [
    ['SSCC', shipment.sscc || 'N/A'],
    ['Origin', shipment.origin || 'N/A'],
    ['Destination', shipment.destination || 'N/A'],
    ['Status', (shipment.status || 'N/A').replace('_', ' ').toUpperCase()],
    ['Device ID', shipment.device_id || 'N/A'],
    ['Created', shipment.created_at ? new Date(shipment.created_at).toLocaleString() : 'N/A'],
  ];
  
  autoTable(doc, {
    startY: 62,
    head: [],
    body: details,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40, textColor: [100, 116, 139] },
      1: { cellWidth: 100 }
    },
    margin: { left: 20, right: 20 }
  });
  
  // Temperature Compliance Section
  const tempY = doc.previousAutoTable.finalY + 15;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Temperature Compliance', 20, tempY);
  doc.line(20, tempY + 3, pageWidth - 20, tempY + 3);
  
  const tempMin = shipment.expected_temp_min ?? -20;
  const tempMax = shipment.expected_temp_max ?? 8;
  
  // Generate sample temperature readings if none provided
  const temps = temperatureData.length > 0 ? temperatureData : 
    Array.from({ length: 24 }, (_, i) => ({
      time: new Date(Date.now() - (23 - i) * 3600000).toISOString(),
      temperature: tempMin + Math.random() * (tempMax - tempMin + 2) - 1
    }));
  
  const actualTemps = temps.map(t => t.temperature);
  const minTemp = Math.min(...actualTemps);
  const maxTemp = Math.max(...actualTemps);
  const avgTemp = actualTemps.reduce((a, b) => a + b, 0) / actualTemps.length;
  const violations = actualTemps.filter(t => t < tempMin || t > tempMax).length;
  const compliant = violations === 0;
  
  // Compliance Status Box
  const statusY = tempY + 10;
  if (compliant) {
    doc.setFillColor(220, 252, 231);
  } else {
    doc.setFillColor(254, 226, 226);
  }
  doc.roundedRect(20, statusY, pageWidth - 40, 25, 3, 3, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  if (compliant) {
    doc.setTextColor(22, 163, 74);
  } else {
    doc.setTextColor(220, 38, 38);
  }
  doc.text(compliant ? '✓ COMPLIANT' : '✗ NON-COMPLIANT', 30, statusY + 10);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (compliant) {
    doc.setTextColor(21, 128, 61);
  } else {
    doc.setTextColor(185, 28, 28);
  }
  doc.text(`${temps.length} readings | ${violations} violations | Range: ${tempMin}°C to ${tempMax}°C`, 30, statusY + 18);
  
  // Temperature Statistics
  const statsY = statusY + 35;
  
  const statsData = [
    ['Expected Range', `${tempMin}°C to ${tempMax}°C`],
    ['Minimum Recorded', `${minTemp.toFixed(1)}°C`],
    ['Maximum Recorded', `${maxTemp.toFixed(1)}°C`],
    ['Average', `${avgTemp.toFixed(1)}°C`],
    ['Total Readings', temps.length.toString()],
    ['Violations', violations.toString()],
    ['Compliance Rate', `${((temps.length - violations) / temps.length * 100).toFixed(1)}%`],
  ];
  
  autoTable(doc, {
    startY: statsY,
    head: [['Metric', 'Value']],
    body: statsData,
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    styles: { fontSize: 10 },
    margin: { left: 20, right: 20 }
  });
  
  // Temperature Readings Table
  const readingsY = doc.previousAutoTable.finalY + 15;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Temperature Readings (Last 24 Hours)', 20, readingsY);
  doc.line(20, readingsY + 3, pageWidth - 20, readingsY + 3);
  
  const readingsData = temps.slice(-12).map(t => {
    const isViolation = t.temperature < tempMin || t.temperature > tempMax;
    return [
      new Date(t.time).toLocaleString(),
      `${t.temperature.toFixed(1)}°C`,
      isViolation ? 'VIOLATION' : 'OK'
    ];
  });
  
  autoTable(doc, {
    startY: readingsY + 7,
    head: [['Timestamp', 'Temperature', 'Status']],
    body: readingsData,
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    styles: { fontSize: 9 },
    columnStyles: {
      2: { 
        fontStyle: 'bold',
      }
    },
    didParseCell: function(data) {
      if (data.column.index === 2 && data.cell.raw === 'VIOLATION') {
        data.cell.styles.textColor = [220, 38, 38];
      } else if (data.column.index === 2 && data.cell.raw === 'OK') {
        data.cell.styles.textColor = [22, 163, 74];
      }
    },
    margin: { left: 20, right: 20 }
  });
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 20;
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('This report is generated by SmartPallet IoT Platform', 20, footerY);
  doc.text('© 2025 Invig - All Rights Reserved', 20, footerY + 5);
  doc.text(`support@invig.no | +47 40066686`, pageWidth - 60, footerY + 5);
  
  // Save
  const filename = `SmartPallet_Compliance_${shipment.sscc || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
  
  return filename;
};

export const generateShipmentSummary = (shipments) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('SmartPallet - Shipment Summary', 20, 22);
  
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 70, 15);
  doc.text(`Total Shipments: ${shipments.length}`, pageWidth - 70, 22);
  
  // Summary Stats
  const activeCount = shipments.filter(s => s.status === 'in_transit' || s.status === 'activated').length;
  const deliveredCount = shipments.filter(s => s.status === 'delivered' || s.status === 'accepted').length;
  
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(12);
  doc.text(`Active: ${activeCount} | Delivered: ${deliveredCount}`, 20, 50);
  
  // Shipments Table
  const tableData = shipments.map(s => [
    s.sscc?.slice(-8) || 'N/A',
    s.origin || 'N/A',
    s.destination || 'N/A',
    (s.status || 'N/A').replace('_', ' '),
    s.created_at ? new Date(s.created_at).toLocaleDateString() : 'N/A'
  ]);
  
  autoTable(doc, {
    startY: 60,
    head: [['SSCC', 'Origin', 'Destination', 'Status', 'Created']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    styles: { fontSize: 9 },
    margin: { left: 20, right: 20 }
  });
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 15;
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('SmartPallet IoT Platform | © 2025 Invig | support@invig.no', 20, footerY);
  
  doc.save(`SmartPallet_Shipments_${new Date().toISOString().split('T')[0]}.pdf`);
};

export default { generateComplianceReport, generateShipmentSummary };
